import {
  useInitializeCosmicLocalizationContext,
  isCosmicSupportedCurrency,
  isCosmicSupportedLocale,
} from '@taxbit-private/cosmic-localization'
import { useLDClient } from 'launchdarkly-react-client-sdk'
import { useEffect, useState } from 'react'

import { useAuth } from 'contexts/auth/AuthContext'
import { useOrg } from 'contexts/OrgContext'
import { useUser } from 'contexts/UserContext'
import useAccountingSettingsData from 'hooks/data/accounting/accountingSettingsHook'
import { useOrgSetting } from 'hooks/data/settingsHooks'
import useFeatureFlags from 'hooks/feature-flags/useFeatureFlags'
import { isZeusEnvironment } from 'utils/environment'
import { updateDatadogUser } from 'vendors/datadog'
import { updateLaunchDarklyUser } from 'vendors/launchDarkly'
import { initializePendo, updatePendoUser } from 'vendors/pendo'

import getLocalizeLanguage from './localize/getLocalizeLanguage'
import logError from './logError'

export const useInitializeVendorInfo = () => {
  const { user } = useUser()
  const { authManager } = useAuth()
  const { org } = useOrg()

  const isApiReady = !!authManager?.getToken()

  const { value: localeValue } = useOrgSetting('locale', { enabled: isApiReady })

  // Default to en-US if locale can't be loaded (e.g., user lacks SettingOrgRead permission)
  const locale = localeValue || 'en-US'

  const { accountingConfig } = useAccountingSettingsData({ isEnabled: isApiReady })
  // Default to USD if currency can't be loaded (e.g., user lacks accounting permissions)
  const currency = accountingConfig?.base_fiat_currency || 'USD'

  const ldClient = useLDClient()

  const [isInitialized, setIsInitialized] = useState(false)

  const { setLocalizationContext, isCosmicLocalizationInitialized } =
    useInitializeCosmicLocalizationContext()

  const { shouldAutomaticallyTranslate } = useFeatureFlags()

  /**
   * Handles primary initialization of user/org/session info on first load.
   */
  useEffect(() => {
    const hasRequiredDataForInitialization =
      user &&
      org &&
      ldClient &&
      isCosmicSupportedCurrency(currency) &&
      isCosmicSupportedLocale(locale)

    if (!hasRequiredDataForInitialization) {
      setIsInitialized(false)
      return
    }

    void updateLaunchDarklyUser(user, org, ldClient)

    setLocalizationContext({
      currency,
      locale,
    })

    // Google explicitly does not allow Pendo tracking,
    // Pendo has an exclude list that blocks this. This is a hacky solution
    // that safeguards against these metrics being initialized for them.
    // Ideally, we would initialize Pendo at the same time as LD & DD.
    if (user.org !== 'Google' && org !== 'Google' && !isInitialized) {
      initializePendo()
      updatePendoUser(user, org)

      setIsInitialized(true)
    } else {
      setIsInitialized(true)
    }
  }, [user, org, isInitialized, ldClient, setLocalizationContext, currency, locale])

  /**
   * Handles reidentification of org info on org switch after the
   * initial load of user and org info.
   */
  useEffect(() => {
    if (user && org && ldClient && isInitialized) {
      void updateLaunchDarklyUser(user, org, ldClient)
      updatePendoUser(user, org)
      updateDatadogUser(user, org)
    }
    // We don't want this effect to run for user and ldClients loading in,
    // only when the org switches separately from the above effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org])

  // Resets the global currency in the Cosmic Localization Context when this setting changes.
  useEffect(() => {
    setLocalizationContext({
      currency,
    })
  }, [currency, setLocalizationContext])

  // Resets the global locale in the Cosmic Localization Context and Localization when this setting changes.
  useEffect(() => {
    setLocalizationContext({
      locale,
    })

    if (!isZeusEnvironment() && Localize) {
      void getLocalizeLanguage({ locale, shouldAutomaticallyTranslate })
        .then((language) => {
          Localize.setLanguage(language)
        })
        .catch((error: unknown) => {
          logError({
            message: 'Failed to determine correct language for Localize. Falling back to en-US.',
            error,
          })
        })
    }
  }, [currency, locale, setLocalizationContext, shouldAutomaticallyTranslate])

  // In theory, `isInitialized` should be enough to determine if the localization context is initialized as well. However,
  // we are hitting a race condition that leads to a lot of top-level error boundaries due to the checks in the Cosmic
  // Localization context helpers. This is a temporary fix to guarantee that the Cosmic Localization context is initialized
  // until we're able to figure out why the initialization flag isn't fully working.
  return isInitialized && isCosmicLocalizationInitialized
}
